import type { CSSProperties } from 'react';

import Text from '@cartwhl/ui/base/TextNew';

import type {
  ComicPerformanceMetric,
  ComicPerformanceSeries,
} from '../content';
import {
  formatBenchmarkRawValue,
  formatBenchmarkTooltipValue,
} from './benchmark';
import { LABEL_RADIUS, getAxisAngle, getRadialPoint } from './math';

export interface ChartSeries extends ComicPerformanceSeries {
  color: string;
}

interface MetricLabelProps {
  active: boolean;
  buttonRef: (element: HTMLButtonElement | null) => void;
  count: number;
  index: number;
  metric: ComicPerformanceMetric;
  onClick: () => void;
  onBlur: () => void;
  onFocus: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerEnter: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerLeave: (event: React.PointerEvent<HTMLButtonElement>) => void;
  reducedMotion: boolean;
  titleId: string;
}

export function MetricLabel({
  active,
  buttonRef,
  count,
  index,
  metric,
  onClick,
  onBlur,
  onFocus,
  onKeyDown,
  onPointerDown,
  onPointerEnter,
  onPointerLeave,
  reducedMotion,
  titleId,
}: MetricLabelProps) {
  const point = getRadialPoint(index, count, 100, LABEL_RADIUS);
  const angle = getAxisAngle(index, count);
  const desktopAlignment =
    Math.abs(Math.cos(angle)) < 0.4
      ? 'sm:-translate-x-1/2 sm:text-center'
      : Math.cos(angle) < 0
        ? 'sm:-translate-x-full sm:text-right'
        : 'sm:text-left';
  const mobilePositions = [
    'left-1/2 top-0 -translate-x-1/2 text-center',
    'right-0 top-[12%] text-right',
    'right-0 top-[42%] text-right',
    'right-0 top-[70%] text-right',
    'left-1/2 top-[89%] -translate-x-1/2 text-center',
    'left-0 top-[70%] text-left',
    'left-0 top-[42%] text-left',
    'left-0 top-[12%] text-left',
  ][index];
  return (
    <div
      className={`absolute z-10 -translate-y-2 ${mobilePositions} ${desktopAlignment} sm:left-[var(--desktop-left)] sm:top-[var(--desktop-top)]`}
      style={
        {
          '--desktop-left': `${(point.x / 860) * 100}%`,
          '--desktop-top': `${(point.y / 600) * 100}%`,
        } as CSSProperties
      }
    >
      <button
        ref={buttonRef}
        type="button"
        data-chart-interactive="metric"
        aria-expanded={active}
        aria-controls={active ? `${titleId}-${metric.id}-tooltip` : undefined}
        aria-describedby={
          active ? `${titleId}-${metric.id}-tooltip` : undefined
        }
        className={`focus-visible:outline-green flex w-[88px] flex-col gap-1 rounded px-1 py-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 sm:w-auto ${reducedMotion ? '' : 'sm:transition-transform'} ${active ? `opacity-100 ${reducedMotion ? '' : 'sm:scale-105'}` : 'opacity-80 hover:opacity-100'}`}
        onClick={onClick}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
      >
        <Text
          as="span"
          token="12/medium"
          leading="body"
          className="block text-pretty sm:whitespace-nowrap"
        >
          {metric.label}
        </Text>
        <Text
          as="span"
          token="12"
          leading="body"
          className="text-secondary block text-pretty sm:whitespace-nowrap"
        >
          {metric.shortDescription}
        </Text>
      </button>
    </div>
  );
}

interface MetricTooltipProps {
  metric: ComicPerformanceMetric;
  models: readonly ChartSeries[];
}

export function MetricTooltip({ metric, models }: MetricTooltipProps) {
  return (
    <div className="bg-gray-90 w-[min(230px,calc(100vw-48px))] rounded-lg p-3 text-left text-white shadow-lg">
      <Text as="p" token="12/medium" leading="body">
        {metric.label}{' '}
        <Text as="span" token="12" leading="body" className="text-gray-45">
          {metric.shortDescription}
        </Text>
      </Text>
      <Text as="p" token="12" leading="body" className="sr-only">
        {metric.description} Normalized score: higher is better.
      </Text>
      <dl className="mt-2 grid grid-cols-[minmax(0,1fr)_max-content_max-content] items-center gap-x-2 gap-y-1 tabular-nums">
        {models.map((model) => (
          <div key={model.id} className="contents">
            <Text
              as="dt"
              token="12"
              leading="body"
              className="flex items-center gap-2 whitespace-nowrap"
            >
              <span
                className="size-2.5 shrink-0 rounded-full border-2"
                style={{
                  borderColor: model.color,
                  backgroundColor:
                    model.id === 'comic-4' ? model.color : 'transparent',
                }}
                aria-hidden="true"
              />
              {model.label}
            </Text>
            <Text
              as="dd"
              token="12"
              leading="body"
              className="text-right"
              aria-label={`Raw value: ${formatBenchmarkRawValue(
                metric.id,
                model.rawValues[metric.id],
                metric.unit,
              )}`}
            >
              {formatBenchmarkTooltipValue(
                metric.id,
                model.rawValues[metric.id],
              )}
            </Text>
            <Text
              as="dd"
              token="12"
              leading="body"
              className="text-gray-45 text-right"
              aria-label={`Normalized score: ${Math.round(model.scores[metric.id])} out of 100`}
            >
              {Math.round(model.scores[metric.id])}
            </Text>
          </div>
        ))}
      </dl>
    </div>
  );
}

interface LegendControlProps {
  active: boolean;
  buttonRef: (element: HTMLButtonElement | null) => void;
  model: ChartSeries;
  onFocus: () => void;
  onBlur: () => void;
  onPointerEnter: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerLeave: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onToggle: () => void;
  pinned: boolean;
}

export function LegendControl({
  active,
  buttonRef,
  model,
  onBlur,
  onFocus,
  onPointerEnter,
  onPointerLeave,
  onToggle,
  pinned,
}: LegendControlProps) {
  const isHero = model.id === 'comic-4';
  return (
    <button
      ref={buttonRef}
      type="button"
      data-chart-interactive="legend"
      aria-label={`Highlight ${model.label} in the chart`}
      aria-pressed={pinned}
      className={`focus-visible:outline-green relative z-10 flex h-[30px] items-center gap-2 rounded-[4px] px-2.5 focus-visible:outline-2 focus-visible:outline-offset-2 ${active ? 'text-primary' : 'text-secondary'}`}
      onClick={onToggle}
      onFocus={onFocus}
      onBlur={onBlur}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <span
        className={`size-2.5 rounded-full border-2 ${isHero || active ? '' : 'bg-primary'}`}
        style={{
          borderColor: model.color,
          backgroundColor: isHero || active ? model.color : undefined,
        }}
      />
      <Text as="span" token="12/medium" leading="body">
        {model.label}
      </Text>
    </button>
  );
}
